import { useState } from 'react';
import { Modal, Button, Form, CloseButton, InputGroup, Spinner, ListGroup } from 'react-bootstrap';
import { Competitor } from '../../../../models/ProductTypes';
import type {
  CompetitorProductRequest,
  InsertCompetitorProductRequest,
  CompetitorProductSearchResult,
} from '../../../../infrastructure/CompetitorClient';
import { useCompetitor } from '../../../../apps/hooks/competitor/useCompetitor';

interface InsertCompetitorProductModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
  readonly onSubmit: (data: InsertCompetitorProductRequest) => Promise<void>;
}

export default function InsertCompetitorProductModal({ show, onHide, onSubmit }: InsertCompetitorProductModalProps) {
  const { searchCompetitorProducts } = useCompetitor();
  const [competitor, setCompetitor] = useState<Competitor | ''>('');
  const [products, setProducts] = useState<Array<Partial<CompetitorProductRequest>>>([createEmptyProduct()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Search-related states
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CompetitorProductRequest[]>([]);

  function createEmptyProduct(): Partial<CompetitorProductRequest> {
    return {
      sku: '',
      name: '',
      brand: '',
      normalizedName: '',
      productImageUrls: [],
      category: [],
      productLink: '',
      size: '',
      pack: '',
      price: [{ currency: 'USD' }],
    };
  }

  const updatePriceField = (index: number, field: 'originalPrice' | 'salesPrice', rawValue: string) => {
    const updatedProducts = [...products];
    const currentPrice = updatedProducts[index]?.price?.[0] ?? { currency: 'USD' };
    const parsed = rawValue === '' ? undefined : parseFloat(rawValue);
    const nextPrice = {
      ...currentPrice,
      currency: 'USD',
      [field]: Number.isFinite(parsed) ? parsed : undefined,
    };
    updatedProducts[index] = { ...updatedProducts[index], price: [nextPrice] };
    setProducts(updatedProducts);
  };

  const handleAddProduct = () => {
    setProducts([...products, createEmptyProduct()]);
  };

  const handleRemoveProduct = (index: number) => {
    const updatedProducts = [...products];
    updatedProducts.splice(index, 1);
    setProducts(updatedProducts);
  };

  const updateProduct = (index: number, field: keyof CompetitorProductRequest, value: any) => {
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    setProducts(updatedProducts);
  };

  const handleImageUrlChange = (index: number, value: string) => {
    const urls = value
      .split(',')
      .map((url) => url.trim())
      .filter((url) => url);
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index], productImageUrls: urls };
    setProducts(updatedProducts);
  };

  const handleCategoryChange = (index: number, value: string) => {
    const categories = value
      .split(',')
      .map((cat) => cat.trim())
      .filter((cat) => cat);
    const updatedProducts = [...products];
    updatedProducts[index] = { ...updatedProducts[index], category: categories };
    setProducts(updatedProducts);
  };

  const handleSearch = async () => {
    if (!competitor || !searchTerm.trim()) {
      setErrors({ ...errors, search: 'Competitor and search term are required' });
      return;
    }

    setIsSearching(true);
    setErrors({ ...errors, search: '' });

    try {
      const result: CompetitorProductSearchResult = await searchCompetitorProducts(
        competitor as Competitor,
        searchTerm,
      );
      setSearchResults(result.products);
    } catch (error) {
      console.error('Error searching competitor products:', error);
      setErrors({ ...errors, search: 'Failed to search products' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProductFromSearch = (product: CompetitorProductRequest) => {
    // Ensure product has all required fields
    const normalizedProduct: CompetitorProductRequest = {
      ...product,
      normalizedName: product.normalizedName || product.name,
      category: product.category || [],
      size: product.size ?? '',
      pack: product.pack ?? '',
      price: product.price?.length ? product.price : [{ currency: 'USD' }],
    };

    setProducts([...products, normalizedProduct]);
    setSearchResults([]);
    setSearchTerm('');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!competitor) {
      newErrors['competitor'] = 'Competitor is required';
    }

    let isValid = true;
    products.forEach((product, index) => {
      if (!product.sku) {
        newErrors[`product_${index}_sku`] = 'SKU is required';
        isValid = false;
      }
      if (!product.name) {
        newErrors[`product_${index}_name`] = 'Name is required';
        isValid = false;
      }
      if (!product.brand) {
        newErrors[`product_${index}_brand`] = 'Brand is required';
        isValid = false;
      }
      if (!product.productLink) {
        newErrors[`product_${index}_productLink`] = 'Product link is required';
        isValid = false;
      }
      if (!product.productImageUrls?.length) {
        newErrors[`product_${index}_images`] = 'At least one image URL is required';
        isValid = false;
      }
      if (!product.category?.length) {
        newErrors[`product_${index}_category`] = 'At least one category is required';
        isValid = false;
      }
      if (!product.size?.trim()) {
        newErrors[`product_${index}_size`] = 'Size is required';
        isValid = false;
      }
      if (!product.pack?.trim()) {
        newErrors[`product_${index}_pack`] = 'Pack is required';
        isValid = false;
      }
      const priceEntry = product.price?.[0];
      const hasOriginal = typeof priceEntry?.originalPrice === 'number' && priceEntry.originalPrice > 0;
      const hasSales = typeof priceEntry?.salesPrice === 'number' && priceEntry.salesPrice > 0;
      if (!priceEntry || (!hasOriginal && !hasSales)) {
        newErrors[`product_${index}_price`] = 'At least one of Original or Sales price (USD) is required';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid && products.length > 0 && Boolean(competitor);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        competitor: competitor as Competitor,
        products: products as CompetitorProductRequest[],
      });

      // Reset form
      setCompetitor('');
      setProducts([createEmptyProduct()]);
      setSearchResults([]);
      setSearchTerm('');
      onHide();
    } catch (error) {
      console.error('Error submitting competitor product:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Insert Competitor Product Manually</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Competitor*</Form.Label>
            <Form.Select
              value={competitor}
              onChange={(e) => setCompetitor(e.target.value as Competitor)}
              isInvalid={Boolean(errors['competitor'])}
            >
              <option value="">Select competitor</option>
              <option value={Competitor.YesStyle}>YesStyle</option>
              <option value={Competitor.Stylevana}>Stylevana</option>
              <option value={Competitor.Amazon}>Amazon</option>
              <option value={Competitor.OliveYoung}>OliveYoung</option>
              <option value={Competitor.StyleKorean}>StyleKorean</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors['competitor']}</Form.Control.Feedback>
          </Form.Group>

          {/* Search for competitor products */}
          <Form.Group className="mb-3 p-3 border rounded">
            <Form.Label>Search competitor products</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Enter search term (case sensitive)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                isInvalid={Boolean(errors['search'])}
              />
              <Button variant="outline-primary" onClick={handleSearch} disabled={isSearching || !competitor}>
                {isSearching ? <Spinner animation="border" size="sm" /> : 'Search'}
              </Button>
              <Form.Control.Feedback type="invalid">{errors['search']}</Form.Control.Feedback>
            </InputGroup>

            {searchResults.length > 0 && (
              <div className="mt-3">
                <div className="d-flex justify-content-between mb-2">
                  <h6>Search Results</h6>
                  <small className="text-muted">{searchResults.length} products found</small>
                </div>
                <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {searchResults.map((product, idx) => (
                    <ListGroup.Item
                      key={`${product.sku}-${idx}`}
                      action
                      onClick={() => handleAddProductFromSearch(product)}
                      className="d-flex align-items-center gap-3"
                    >
                      {product.productImageUrls?.[0] && (
                        <img
                          src={product.productImageUrls[0]}
                          alt={product.name}
                          style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                        />
                      )}
                      <div>
                        <div className="fw-semibold">{product.name}</div>
                        <div className="small">
                          SKU: {product.sku} | Brand: {product.brand}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
          </Form.Group>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h5>Products*</h5>
              <Button variant="primary" size="sm" onClick={handleAddProduct}>
                Add Product
              </Button>
            </div>
          </div>

          {products.map((product, index) => (
            <div key={index} className="border rounded p-3 mb-3 position-relative">
              {products.length > 1 && (
                <CloseButton className="position-absolute top-0 end-0 m-2" onClick={() => handleRemoveProduct(index)} />
              )}
              <h6>Product {index + 1}</h6>

              <Form.Group className="mb-3">
                <Form.Label>SKU*</Form.Label>
                <Form.Control
                  type="text"
                  value={product.sku || ''}
                  onChange={(e) => updateProduct(index, 'sku', e.target.value)}
                  isInvalid={Boolean(errors[`product_${index}_sku`])}
                />
                <Form.Control.Feedback type="invalid">{errors[`product_${index}_sku`]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Name*</Form.Label>
                <Form.Control
                  type="text"
                  value={product.name || ''}
                  onChange={(e) => updateProduct(index, 'name', e.target.value)}
                  isInvalid={Boolean(errors[`product_${index}_name`])}
                />
                <Form.Control.Feedback type="invalid">{errors[`product_${index}_name`]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Brand*</Form.Label>
                <Form.Control
                  type="text"
                  value={product.brand || ''}
                  onChange={(e) => updateProduct(index, 'brand', e.target.value)}
                  isInvalid={Boolean(errors[`product_${index}_brand`])}
                />
                <Form.Control.Feedback type="invalid">{errors[`product_${index}_brand`]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Product Link*</Form.Label>
                <Form.Control
                  type="text"
                  value={product.productLink || ''}
                  onChange={(e) => updateProduct(index, 'productLink', e.target.value)}
                  isInvalid={Boolean(errors[`product_${index}_productLink`])}
                />
                <Form.Control.Feedback type="invalid">{errors[`product_${index}_productLink`]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Images</Form.Label>
                <Form.Control
                  type="text"
                  value={product.productImageUrls?.join(',') || ''}
                  onChange={(e) => handleImageUrlChange(index, e.target.value)}
                  isInvalid={Boolean(errors[`product_${index}_images`])}
                />
                <Form.Control.Feedback type="invalid">{errors[`product_${index}_images`]}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Control
                  type="text"
                  value={product.category?.join(',') || ''}
                  onChange={(e) => handleCategoryChange(index, e.target.value)}
                  isInvalid={Boolean(errors[`product_${index}_category`])}
                />
                <Form.Control.Feedback type="invalid">{errors[`product_${index}_category`]}</Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex gap-3">
                <Form.Group className="mb-3 flex-fill">
                  <Form.Label>Size*</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 50ml, 100g"
                    value={product.size || ''}
                    onChange={(e) => updateProduct(index, 'size', e.target.value)}
                    isInvalid={Boolean(errors[`product_${index}_size`])}
                  />
                  <Form.Control.Feedback type="invalid">{errors[`product_${index}_size`]}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3 flex-fill">
                  <Form.Label>Pack*</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. 1, 2, 3"
                    value={product.pack || ''}
                    onChange={(e) => updateProduct(index, 'pack', e.target.value)}
                    isInvalid={Boolean(errors[`product_${index}_pack`])}
                  />
                  <Form.Control.Feedback type="invalid">{errors[`product_${index}_pack`]}</Form.Control.Feedback>
                </Form.Group>
              </div>

              <Form.Group className="mb-1">
                <Form.Label>Price (USD)*</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Original price"
                    value={product.price?.[0]?.originalPrice ?? ''}
                    onChange={(e) => updatePriceField(index, 'originalPrice', e.target.value)}
                    isInvalid={Boolean(errors[`product_${index}_price`])}
                  />
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Sales price"
                    value={product.price?.[0]?.salesPrice ?? ''}
                    onChange={(e) => updatePriceField(index, 'salesPrice', e.target.value)}
                    isInvalid={Boolean(errors[`product_${index}_price`])}
                  />
                </div>
                {errors[`product_${index}_price`] && (
                  <div className="text-danger small mt-1">{errors[`product_${index}_price`]}</div>
                )}
                <Form.Text className="text-muted">Enter at least one of Original or Sales price in USD.</Form.Text>
              </Form.Group>
            </div>
          ))}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
